import { primalKnowledgeProfile } from '../../../shared/rules/primalKnowledge.mjs';
import React, { useState, useEffect, useMemo } from "react";
import apiFetch from '../../../utils/apiFetch';
import { Modal, Alert, Form } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import useUser from '../../../hooks/useUser';
import { SKILLS } from '../skillSchema';
import proficiencyBonus from '../../../utils/proficiencyBonus';
import { isClassFeatureDisplayable } from '../../../shared/rules/classFeatureSupport.mjs';
import { normalizeClassId } from '../../../shared/rules/classIdentity.mjs';
import { levelImprovements, spellSlotGains } from '../utils/levelImprovements';
import LevelUpSpellbookChoice from './LevelUpSpellbookChoice';
import LevelUpSpellChoice from './LevelUpSpellChoice';
import { levelUpSpellRoom, levelUpSpellSelection } from '../utils/levelUpSpells';
import { getSpellbook, spellbookProblem, spellbookRoom } from '../../../shared/rules/wizard.mjs';
import spellData from '../../../data/spells';
import { SCHOLAR_SKILLS } from '../../../shared/rules/classExpertise';
import { getClassSpellLimits } from '../../../shared/rules/spellPreparation';
import { derivePendingSubclassChoices, getSubclassFeatureEntitlements, getSubclassesForClass } from '../../../shared/rules/subclasses/index.mjs';
import { HUNTERS_PREY_OPTIONS } from '../../../shared/rules/subclasses/ranger/huntersPrey.mjs';
import { LANDS, LAND_SUBCLASS_ID } from '../../../shared/rules/subclasses/druid/land.mjs';
import { beastShapeLimit, getKnownForms } from '../../../shared/rules/beastForm';
import { KnownFormsChooser } from './DruidChoiceChooser';
import { FIGHTING_STYLE_CLASSES, STYLE_CANTRIP_COUNT, getCantripStyle, fightingStyleClassLabel, getFightingStyleOptions, getClassFightingStyle, hasFightingStyleFeature } from '../../../shared/rules/fightingStyle.mjs';
import { CLASS_CANTRIPS } from './FightingStyleChooser';
import { calculateFeatPointsLeft } from '../../../utils/featUtils';
import { getPendingFeatureChoices } from '../utils/featureChoices';
import { calculateHitPointConModifier } from '../utils/characterMetrics';
import { DEFT_EXPLORER_LANGUAGE_COUNT, hasRangerFeature } from '../../../shared/rules/ranger.mjs';
import { getCharacterLanguages, LANGUAGE_OPTIONS } from '../../../shared/rules/languages.mjs';
import { ModalShell, ModalHeader, ModalBody, ModalFooter, SearchBar, Button } from '../common/HudPrimitives';
import LevelUpFeatChoice from './LevelUpFeatChoice';
import LevelUpMasteryChoice from './LevelUpMasteryChoice';
import { getAvailableLevelUpClasses, getAvailableNewClasses, getCharacterTotalLevel, getMulticlassSummary, validateAddClassSelection, validateLevelUpSelection } from './characterProgression';

export default function LevelUp({ show, handleClose, form }) {
  const params = useParams();
  const navigate = useNavigate();
  const user = useUser();
  const [step, setStep] = useState('choose-progression');
  const [selectedExistingClassId, setSelectedExistingClassId] = useState('');
  const [selectedNewClassId, setSelectedNewClassId] = useState('');
  const [classRecords, setClassRecords] = useState([]);
  const [search, setSearch] = useState('');
  const [notification, setNotification] = useState('');
  const [error, setError] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [primalKnowledgeSkill, setPrimalKnowledgeSkill] = useState('');
  const [subclassChoice, setSubclassChoice] = useState('');
  const [huntersPreyChoice, setHuntersPreyChoice] = useState('');
  const [landChoice, setLandChoice] = useState('');
  const [formDraft, setFormDraft] = useState({ picks: null, ready: false });
  const [styleChoice, setStyleChoice] = useState('');
  const [styleCantrips, setStyleCantrips] = useState(() => Array(STYLE_CANTRIP_COUNT).fill(''));
  const [deftLanguages, setDeftLanguages] = useState(() => Array(DEFT_EXPLORER_LANGUAGE_COUNT).fill(''));
  const [deftExpertise, setDeftExpertise] = useState('');
  const [scholarExpertise, setScholarExpertise] = useState('');
  const [spellbookPicks, setSpellbookPicks] = useState([]);
  const [spellPicks, setSpellPicks] = useState({ cantrips: [], spells: [] });
  const [previewFeatures, setPreviewFeatures] = useState({ key: '', features: [], loading: false });
  // A feat and new Weapon Mastery weapons the level grants, chosen here (or later in their own menus).
  const [levelFeat, setLevelFeat] = useState(null);
  const [masterySelections, setMasterySelections] = useState(null);

  const totalLevel = getCharacterTotalLevel(form);
  const levelUpClasses = useMemo(() => getAvailableLevelUpClasses(form), [form]);
  const newClasses = useMemo(() => getAvailableNewClasses(form, classRecords), [form, classRecords]);
  const filteredNewClasses = useMemo(() => newClasses.filter((entry) => entry.name.toLowerCase().includes(search.toLowerCase())), [newClasses, search]);
  const selectedExisting = levelUpClasses.find((entry) => entry.name === selectedExistingClassId);
  const selectedNew = newClasses.find((entry) => entry.name === selectedNewClassId);
  const isAdding = step === 'add-class' || step === 'add-review';
  // The class being gained: the next level of an existing class, or level 1 of a new one.
  const target = useMemo(() => {
    if (!isAdding) return selectedExisting;
    if (!selectedNew || selectedNew.disabled) return null;
    const die = selectedNew.hitDie;
    return { name: selectedNew.name, level: 0, nextLevel: 1, hitDie: die == null || die === '' ? null : String(die).startsWith('d') ? String(die) : `d${die}` };
  }, [isAdding, selectedExisting, selectedNew]);

  const resetFlow = () => {
    setStep('choose-progression');
    setSelectedExistingClassId('');
    setSelectedNewClassId('');
    setSearch('');
    setNotification('');
    setError('');
    setValidationError('');
    setIsSubmitting(false);
    setPrimalKnowledgeSkill('');
    setSubclassChoice('');
    setScholarExpertise('');
    setSpellbookPicks([]);
    setSpellPicks({ cantrips: [], spells: [] });
    setLevelFeat(null);
    setMasterySelections(null);
    setHuntersPreyChoice('');
    setLandChoice('');
    setFormDraft({ picks: null, ready: false });
    setStyleChoice('');
    setStyleCantrips(Array(STYLE_CANTRIP_COUNT).fill(''));
    setDeftLanguages(Array(DEFT_EXPLORER_LANGUAGE_COUNT).fill(''));
    setDeftExpertise('');
  };

  useEffect(() => {
    if (!show) resetFlow();
  }, [show]);

  // Most characters have one class, so start with the first one that can still level.
  useEffect(() => {
    if (!show || step !== 'choose-progression' || selectedExistingClassId) return;
    const first = levelUpClasses.find((entry) => !entry.disabled);
    if (first) setSelectedExistingClassId(first.name);
  }, [show, step, selectedExistingClassId, levelUpClasses]);

  useEffect(() => {
    if (!user || !show) return;
    async function fetchData() {
      try {
        const response = await apiFetch('/classes');
        if (!response.ok) {
          setError(`Class catalogue failed to load: ${response.statusText}`);
          return;
        }
        const record = await response.json();
        setClassRecords(Object.values(record || {}));
      } catch (err) {
        setError('Class catalogue failed to load.');
      }
    }
    fetchData();
  }, [show, user]);

  // Features the selected class gains at its next level, from the same data as the Features menu.
  const targetName = target?.name;
  const targetLevel = target?.nextLevel;
  useEffect(() => {
    if (!show || !targetName || !targetLevel || target?.disabled) return undefined;
    const key = `${targetName}:${targetLevel}`;
    let cancelled = false;
    setPreviewFeatures({ key, features: [], loading: true });
    (async () => {
      try {
        const response = await apiFetch(`/classes/${targetName.toLowerCase()}/features/${targetLevel}`);
        const data = response?.ok ? await response.json() : {};
        const features = (data?.features || [])
          .filter((feature) => feature.name && isClassFeatureDisplayable({ ...feature, class: targetName }));
        if (!cancelled) setPreviewFeatures({ key, features, loading: false });
      } catch (err) {
        if (!cancelled) setPreviewFeatures({ key, features: [], loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [show, targetName, targetLevel, target?.disabled]);

  const close = () => {
    resetFlow();
    handleClose?.();
  };

  const primalProfile = primalKnowledgeProfile(form);
  const requiresBarbarianPrimalSkill = selectedExistingClassId === 'Barbarian' && selectedExisting?.nextLevel === 3 && !primalProfile.skill && primalProfile.available.length > 0;
  const barbarianValidation = requiresBarbarianPrimalSkill && !primalKnowledgeSkill ? 'Choose a Primal Knowledge skill before leveling Barbarian.' : '';

  const levelExisting = async () => {
    const validation = validateLevelUpSelection(form, selectedExistingClassId);
    if (!validation.valid || barbarianValidation) {
      setValidationError(barbarianValidation || validation.message);
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    const selectedOccupationObject = form.occupation.find((occupation) => occupation.Occupation === selectedExistingClassId);
    const newLevel = Number(selectedOccupationObject.Level) + 1;
    // The server rolls the Hit Die and updates HP; the client never sends an HP value.
    const updatedLevelForm = { selectedOccupation: selectedExistingClassId, level: newLevel };
    if (requiresBarbarianPrimalSkill) updatedLevelForm.primalKnowledgeSkill = primalKnowledgeSkill;
    try {
      const response = await apiFetch(`/characters/update-level/${params.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(updatedLevelForm) });
      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Level up failed.");
    } catch (err) {
      setError(err.message || 'Level up failed. Please try again.');
      setIsSubmitting(false);
      return;
    }
    // These endpoints need the new class level, so they are saved after the level, in order.
    const put = async (url, body, fallback) => {
      const response = await apiFetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || fallback);
    };
    const followUps = [
      subclassChoice && { run: () => put(`/characters/${params.id}/classes/${selectedClassId}/subclass`, { subclassId: subclassChoice }, 'Subclass selection failed.'),
        failure: (message) => `The subclass wasn't saved: ${message} You can choose it in Character Overview.` },
      styleChanged && { run: () => put(`/characters/${params.id}/classes/${styleClassId}/fighting-style`, styleRequest, 'Fighting Style was not saved.'),
        failure: (message) => `The Fighting Style wasn't saved: ${message} You can choose it in Character Features.` },
      choosesLand && landChoice && { run: () => put(`/characters/${params.id}/classes/druid/land`, { landId: landChoice }, 'The land was not saved.'),
        failure: (message) => `The land wasn't saved: ${message} You can choose it in Character Features.` },
      gainsForms && formPicks && { run: () => put(`/characters/${params.id}/druid/known-forms`, { forms: formPicks }, 'The Wild Shape forms were not saved.'),
        failure: (message) => `The Wild Shape forms weren't saved: ${message} You can choose them in Character Features.` },
      choosesHuntersPrey && huntersPreyChoice && { run: () => put(`/characters/${params.id}/classes/ranger/hunters-prey`, { preyId: huntersPreyChoice }, "Hunter's Prey was not saved."),
        failure: (message) => `Hunter's Prey wasn't saved: ${message} You can choose it in Character Features.` },
      unlocksDeftExplorer && deftLanguagesReady && { run: () => put(`/characters/${params.id}/classes/ranger/languages`, { languages: deftLanguages }, 'Languages were not saved.'),
        failure: (message) => `The Deft Explorer languages weren't saved: ${message} You can choose them in Character Overview.` },
      unlocksDeftExplorer && deftExpertise && { run: () => put(`/skills/update-skills/${params.id}`, { skill: deftExpertise, proficient: true, expertise: true }, 'Expertise was not saved.'),
        failure: (message) => `The Deft Explorer Expertise wasn't saved: ${message} You can choose it in Skills.` },
      gainsFeat && levelFeat?.ready && { run: () => put(`/feats/update/${params.id}`, { feat: [...(form.feat || []), levelFeat.entry], expectedRevision: form.featRevision || 0 }, 'The feat was not saved.'),
        failure: (message) => `The feat wasn't saved: ${message} You can choose it in Feats.` },
      unlocksScholar && scholarExpertise && { run: () => put(`/skills/update-skills/${params.id}`, { skill: scholarExpertise, proficient: true, expertise: true }, 'Expertise was not saved.'),
        failure: (message) => `The Scholar Expertise wasn't saved: ${message} You can choose it in Skills.` },
      gainsSpellbook && spellbookPicks.length > 0 && { run: () => put(`/characters/${params.id}/classes/wizard/spellbook`, { spells: [...getSpellbook(form), ...spellbookPicks] }, 'The spellbook was not saved.'),
        failure: (message) => `The new spells weren't saved: ${message} You can add them on the Spellcasting card in Character Features.` },
      gainsSpells && spellPickCount > 0 && { run: () => put(spellSelectionUrl, { spells: spellSelection }, 'The new spells were not saved.'),
        failure: (message) => `The new spells weren't saved: ${message} You can choose them on the Spells page.` },
      gainsMastery && masterySelections && { run: () => put(`/characters/${params.id}/weapon-masteries/${selectedClassId}`, { selections: masterySelections }, 'Weapon Mastery was not saved.'),
        failure: (message) => `The Weapon Mastery weapons weren't saved: ${message} You can choose them in Character Features.` },
    ].filter(Boolean);
    for (const followUp of followUps) {
      try {
        await followUp.run();
      } catch (err) {
        setNotification(`${form.name || 'Character'} is now a Level ${newLevel} ${selectedExistingClassId}.`);
        setError(followUp.failure(err.message));
        setStep('done');
        setIsSubmitting(false);
        return;
      }
    }
    setNotification(`${form.name || 'Character'} is now a Level ${newLevel} ${selectedExistingClassId}.`);
    navigate(0);
  };

  const addNewClass = async () => {
    const validation = validateAddClassSelection(form, classRecords, selectedNewClassId);
    if (!validation.valid) {
      setValidationError(validation.message);
      return;
    }
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const response = await apiFetch(`/characters/multiclass/${params.id}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ newOccupation: selectedNewClassId }) });
      const data = await response.json();
      if (response.ok) {
        setNotification(`${form.name || 'Character'} added ${selectedNewClassId} and is now Level ${totalLevel + 1}.`);
        if (styleChanged) {
          const saved = await apiFetch(`/characters/${params.id}/classes/${styleClassId}/fighting-style`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(styleRequest) });
          if (!saved.ok) {
            const failure = await saved.json().catch(() => ({}));
            setError(`The Fighting Style wasn't saved: ${failure.message || 'Fighting Style was not saved.'} You can choose it in Character Features.`);
            setStep('done');
            setIsSubmitting(false);
            return;
          }
        }
        if (gainsSpells && spellPickCount > 0) {
          const saved = await apiFetch(spellSelectionUrl, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ spells: spellSelection }) });
          if (!saved.ok) {
            const failure = await saved.json().catch(() => ({}));
            setError(`The new spells weren't saved: ${failure.message || 'The new spells were not saved.'} You can choose them on the Spells page.`);
            setStep('done');
            setIsSubmitting(false);
            return;
          }
        }
        navigate(0);
      } else if (response.status === 400) {
        setValidationError(data.message ? `Can't add ${selectedNewClassId}: ${data.message}.` : 'Multiclass validation failed');
        setIsSubmitting(false);
      } else {
        setError(data.message || 'Database update failed');
        setIsSubmitting(false);
      }
    } catch (err) {
      setError('Database update failed');
      setIsSubmitting(false);
    }
  };

  const skillLabels = Object.fromEntries(SKILLS.map((skill) => [skill.key, skill.label]));
  const availablePrimalSkills = primalProfile.available;

  const currentProficiency = proficiencyBonus(totalLevel);
  const nextProficiency = proficiencyBonus(totalLevel + 1);
  const selectedClassId = normalizeClassId(target?.name);
  const nextForm = !target ? form
    : isAdding ? { ...form, occupation: [...(form.occupation || []), { Occupation: target.name, Level: 1 }] }
      : { ...form, occupation: (form.occupation || []).map((entry) => (entry.Occupation === target.name ? { ...entry, Level: target.nextLevel } : entry)) };
  // Reuse the subclass rule: a choice appears at the new level that isn't pending today.
  const unlocksSubclass = Boolean(target) && !derivePendingSubclassChoices(form).some((choice) => choice.classId === selectedClassId)
    && derivePendingSubclassChoices(nextForm).some((choice) => choice.classId === selectedClassId);
  const subclassOptions = unlocksSubclass && !isAdding ? getSubclassesForClass(selectedClassId).filter(({ selectionLevel }) => selectionLevel <= target.nextLevel) : [];
  const chosenSubclass = subclassOptions.find((option) => option.id === subclassChoice);
  // Hunter (Ranger 3) also chooses Hunter's Prey; it is saved right after the subclass.
  const choosesHuntersPrey = selectedClassId === 'ranger' && subclassChoice === 'hunter';
  const chosenPrey = HUNTERS_PREY_OPTIONS.find((option) => option.id === huntersPreyChoice);
  // Circle of the Land (Druid 3) also chooses a land; it is saved right after the subclass.
  const choosesLand = selectedClassId === 'druid' && subclassChoice === LAND_SUBCLASS_ID;
  const chosenLand = LANDS[landChoice] || null;
  // Wild Shape (Druid 2, 4 and 8): the new level has room for more known forms, chosen from its Beasts.
  const formPicks = formDraft.ready ? formDraft.picks : null;
  const gainsForms = Boolean(target) && !isAdding && selectedClassId === 'druid'
    && (beastShapeLimit(target.nextLevel)?.forms || 0) > getKnownForms(form).length;
  // Fighting Style (Fighter 1, Ranger 2): choose it when the class grants it, or replace it on a new level of that class.
  const styleClassId = target && FIGHTING_STYLE_CLASSES[selectedClassId] ? selectedClassId : null;
  const currentStyle = styleClassId ? getClassFightingStyle(form, styleClassId) : null;
  const offersStyle = Boolean(styleClassId) && hasFightingStyleFeature(nextForm, styleClassId);
  const styleOptions = offersStyle ? getFightingStyleOptions(styleClassId) : [];
  const chosenStyle = styleOptions.find((style) => style.id === styleChoice) || null;
  // Druidic Warrior (Ranger) and Blessed Warrior (Paladin) also take two cantrips from another class's list.
  const cantripStyle = getCantripStyle(chosenStyle?.id);
  const styleCantripOptions = cantripStyle ? CLASS_CANTRIPS[cantripStyle.spellList] : [];
  const currentCantrips = getCantripStyle(currentStyle?.id) && currentStyle.id === chosenStyle?.id ? currentStyle.cantrips || [] : [];
  const cantripChanges = currentCantrips.length ? styleCantrips.filter((id) => !currentCantrips.includes(id)).length : 0;
  const styleChanged = Boolean(chosenStyle) && (chosenStyle.id !== currentStyle?.id || (Boolean(cantripStyle) && cantripChanges > 0));
  const styleError = !cantripStyle ? ''
    : !styleCantrips.every(Boolean) || new Set(styleCantrips).size !== styleCantrips.length ? `Choose two different ${cantripStyle.listLabel} cantrips.`
      : currentCantrips.length && cantripChanges > 1 ? `Replace at most one ${cantripStyle.listLabel} cantrip.` : '';
  const styleRequest = chosenStyle ? { styleId: chosenStyle.id, expectedStyleId: currentStyle?.id || null, ...(cantripStyle ? { cantrips: styleCantrips } : {}) } : null;
  const cantripName = (id) => styleCantripOptions.find((spell) => spell.id === id)?.name || id;
  const styleSummary = chosenStyle ? `${chosenStyle.name}${cantripStyle ? ` (${styleCantrips.map(cantripName).join(', ')})` : ''}`
    : currentStyle ? `Keep ${currentStyle.name}` : 'Decide later in Character Features';
  const renderStyleChoice = () => (
    <div className="levelup-choice" role="group" aria-label="Fighting Style">
      <span className="form-label">Fighting Style</span>
      <p className="levelup-choice__hint">{currentStyle
        ? `You can replace ${currentStyle.name} because you gain a ${fightingStyleClassLabel(styleClassId)} level.`
        : 'Optional now: you can also choose it in Character Features.'}</p>
      <Form.Group className="mb-2" controlId="levelup-fighting-style">
        <Form.Label>Style</Form.Label>
        <Form.Select value={styleChoice} onChange={(event) => {
          const value = event.target.value;
          setStyleChoice(value);
          if (getCantripStyle(value)) setStyleCantrips(value === currentStyle?.id && currentStyle.cantrips?.length ? [...currentStyle.cantrips] : Array(STYLE_CANTRIP_COUNT).fill(''));
        }}>
          <option value="">{currentStyle ? `Keep ${currentStyle.name}` : 'Decide later'}</option>
          {styleOptions.map((style) => <option key={style.id} value={style.id}>{style.name}</option>)}
        </Form.Select>
      </Form.Group>
      {chosenStyle && <p className="levelup-choice__hint">{chosenStyle.description}</p>}
      {cantripStyle && styleCantrips.map((value, index) => (
        <Form.Group className="mb-2" controlId={`levelup-druid-cantrip-${index}`} key={index}>
          <Form.Label>{`${cantripStyle.listLabel} cantrip ${index + 1}`}</Form.Label>
          <Form.Select value={value} onChange={(event) => setStyleCantrips((previous) => previous.map((entry, i) => (i === index ? event.target.value : entry)))}>
            <option value="">Choose a cantrip</option>
            {styleCantripOptions.map((spell) => <option key={spell.id} value={spell.id} disabled={styleCantrips.includes(spell.id) && value !== spell.id}>{spell.name}</option>)}
          </Form.Select>
        </Form.Group>
      ))}
      {styleError && <p className="text-danger small" role="alert">{styleError}</p>}
    </div>
  );
  // Choices left for their own menus after leveling (those menus glow until done).
  const currentPendingFeatures = getPendingFeatureChoices(form);
  const gainsFeat = Boolean(target) && !isAdding
    && calculateFeatPointsLeft(nextForm.occupation, form.feat, form.abilityScoreImprovement) > calculateFeatPointsLeft(form.occupation, form.feat, form.abilityScoreImprovement);
  const gainsMastery = Boolean(target) && !isAdding && getPendingFeatureChoices(nextForm).has(`weapon-mastery:${selectedClassId}`)
    && !currentPendingFeatures.has(`weapon-mastery:${selectedClassId}`);
  const featValidation = gainsFeat && levelFeat && !levelFeat.ready ? levelFeat.reason : '';
  // A Wizard with a saved book adds this level's spells here. A subclass chosen just now counts toward the room.
  const spellbookForm = !target || selectedClassId !== 'wizard' ? nextForm
    : { ...nextForm, classState: { ...nextForm.classState, wizard: { ...nextForm.classState?.wizard, ...(subclassChoice ? { subclassId: subclassChoice } : {}) } } };
  const gainsSpellbook = Boolean(target) && !isAdding && selectedClassId === 'wizard' && Boolean(getSpellbook(form)) && getPendingFeatureChoices(spellbookForm).has('wizard-spellbook');
  const spellbookOpen = gainsSpellbook ? spellbookRoom(spellbookForm, getSpellbook(form), spellData).total - spellbookRoom(spellbookForm, getSpellbook(form), spellData).used : 0;
  const spellbookInvalid = spellbookPicks.length > 0 && Boolean(spellbookProblem(spellbookForm, [...(getSpellbook(form) || []), ...spellbookPicks], spellData));
  // New cantrips and prepared spells, for every casting class. A Wizard added by multiclassing sets up a spellbook first.
  const spellRoom = target && getClassSpellLimits(selectedClassId, target.nextLevel).preparedSpells > 0 ? levelUpSpellRoom(form, nextForm, selectedClassId, target.nextLevel, spellData, spellbookPicks) : null;
  const gainsSpells = Boolean(spellRoom) && !(isAdding && selectedClassId === 'wizard')
    && ((spellRoom.cantrips.open > 0 && spellRoom.cantrips.options.length > 0) || (spellRoom.spells.open > 0 && spellRoom.spells.options.length > 0));
  const spellPickCount = spellPicks.cantrips.length + spellPicks.spells.length;
  const spellSelection = gainsSpells ? levelUpSpellSelection(form, selectedClassId, spellPicks, spellRoom) : [];
  const spellSelectionUrl = `/characters/${params.id}/spell-sources/class/${selectedClassId}/spells`;
  // Scholar (Wizard 2): one Expertise from its six skills, optional here like Deft Explorer's.
  const unlocksScholar = Boolean(target) && !isAdding && selectedClassId === 'wizard' && target.nextLevel === 2;
  const laterReminders = target ? [
    gainsFeat && !levelFeat?.ready && 'Choose a feat or Ability Score Improvement in Feats',
    [...getPendingFeatureChoices(nextForm)].some((key) => key.startsWith('weapon-mastery:') && !currentPendingFeatures.has(key)) && !(gainsMastery && masterySelections)
      && 'Choose more Weapon Mastery weapons in Character Features',
    [...getPendingFeatureChoices(nextForm)].some((key) => key.startsWith('fighting-style') && !currentPendingFeatures.has(key)) && !chosenStyle
      && 'Choose a Fighting Style in Character Features',
    choosesHuntersPrey && !huntersPreyChoice && "Choose Hunter's Prey in Character Features",
    choosesLand && !landChoice && 'Choose your land in Character Features',
    gainsForms && !formPicks && 'Choose your Wild Shape forms in Character Features',
    gainsSpellbook && spellbookPicks.length < spellbookOpen && 'Add new spells on the Spellcasting card in Character Features',
    gainsSpells && spellPickCount < spellRoom.cantrips.open + spellRoom.spells.open && 'Choose the rest of your spells on the Spells page',
    unlocksScholar && !scholarExpertise && 'Choose your Scholar Expertise in Skills',
  ].filter(Boolean) : [];
  // Deft Explorer (Ranger 2): an Expertise skill and two languages, each optional now.
  const unlocksDeftExplorer = Boolean(target) && !isAdding && selectedClassId === 'ranger'
    && !hasRangerFeature(form, 'deftExplorer') && hasRangerFeature(nextForm, 'deftExplorer');
  const knownLanguages = getCharacterLanguages(form);
  const deftLanguageOptions = LANGUAGE_OPTIONS.filter((language) => !knownLanguages.includes(language));
  const deftLanguagesReady = deftLanguages.every(Boolean) && new Set(deftLanguages).size === deftLanguages.length;
  const skillGranted = (skill) => [form.skills, form.race?.skills, form.background?.skills, ...(form.feat || []).map((feat) => feat?.skills)]
    .some((source) => source?.[skill]?.proficient);
  const deftExpertiseOptions = SKILLS.filter(({ key }) => skillGranted(key) && !form.skills?.[key]?.expertise);
  const scholarOptions = SKILLS.filter(({ key }) => SCHOLAR_SKILLS.includes(key) && skillGranted(key) && !form.skills?.[key]?.expertise);
  const hasChoices = requiresBarbarianPrimalSkill || subclassOptions.length > 0 || gainsForms || unlocksDeftExplorer || (offersStyle && !isAdding) || gainsFeat || gainsMastery || gainsSpellbook || gainsSpells || unlocksScholar;
  const previewKey = target ? `${target.name}:${target.nextLevel}` : '';
  // The subclass rows are placeholders, so the registry supplies what the subclass gives at this level, chosen just now or earlier.
  const subclassGains = target ? [...getSubclassFeatureEntitlements(nextForm), ...(chosenSubclass?.features || []).filter((feature) => !feature.choice)]
    .filter((feature, index, all) => feature.level === target.nextLevel && (feature.source?.classId || chosenSubclass?.classId) === selectedClassId
      && all.findIndex((entry) => entry.id === feature.id) === index) : [];
  const gainedFeatures = [...(previewFeatures.key === previewKey ? previewFeatures.features : []), ...subclassGains];
  const featuresLoading = previewFeatures.key !== previewKey || previewFeatures.loading;
  const levelUpLabel = target ? `Level up to ${target.name} ${target.nextLevel}` : 'Choose a class';
  // 2024 rules: adding a class needs its ability minimum and that of every current class.
  const requirementFor = (name) => newClasses.find((entry) => entry.name === name)?.multiclassRequirement;
  const multiclassRequirements = isAdding && target
    ? [target.name, ...levelUpClasses.map((entry) => entry.name)]
      .map((name) => (requirementFor(name) ? `${requirementFor(name)} (${name})` : null))
      .filter(Boolean)
    : [];
  const requirementNote = multiclassRequirements.length ? `Needs ${multiclassRequirements.join(' and ')}, checked when you confirm.` : '';

  const STEPS = [
    { key: 'choose-progression', label: 'Class' },
    { key: 'choices', label: 'Choices' },
    { key: 'review', label: 'Review' },
  ];
  const stepIndex = step === 'add-class' ? 0 : step === 'add-review' ? 2 : STEPS.findIndex((entry) => entry.key === step);
  const goNext = () => setStep(step === 'choose-progression' && hasChoices ? 'choices' : 'review');
  const goBack = () => setStep(step === 'review' && hasChoices ? 'choices' : 'choose-progression');

  // Shows how HP grows (the die is rolled on the server), never the rolled amount.
  const conModifier = calculateHitPointConModifier(form);
  const hitPointFormula = `${target?.hitDie || 'Hit Die'} ${conModifier < 0 ? '−' : '+'} ${Math.abs(conModifier)} Con`;

  // The review steps add what each feature does; the class step stays a short list.
  const improvements = target ? levelImprovements(form, nextForm, target.name) : [];
  const slotGains = target ? spellSlotGains(form, nextForm, target.name) : null;
  const slotChip = ({ level, count, gained, raised }, label) => <span key={label} className={`levelup-slot${gained > 0 || raised ? ' is-new' : ''}`}>{label} {level}: {count}{gained > 0 ? ` (+${gained})` : ''}</span>;
  const renderGains = ({ details = false } = {}) => (
    <ul className="levelup-preview__list">
      <li><i className="fa-solid fa-heart" aria-hidden="true"></i>{hitPointFormula}</li>
      {nextProficiency !== currentProficiency && <li><i className="fa-solid fa-arrow-up" aria-hidden="true"></i>Proficiency bonus +{currentProficiency} → +{nextProficiency}</li>}
      {featuresLoading ? <li className="levelup-preview__muted">Loading features…</li> : gainedFeatures.map(({ name, description }) => <li key={name}><i className="fa-solid fa-star" aria-hidden="true"></i>
        <span>{name}{details && description && <small className="levelup-preview__detail">{description}</small>}</span></li>)}
      {improvements.map(({ label, from, to }) => <li key={label}><i className="fa-solid fa-arrow-up" aria-hidden="true"></i>{label} {from} → {to}</li>)}
      {slotGains && <li><i className="fa-solid fa-wand-sparkles" aria-hidden="true"></i><span><span className="levelup-slots__label">Spell slots</span>
        <span className="levelup-slots">{slotGains.slots.map((slot) => slotChip(slot, 'Level'))}{slotGains.pact && slotChip(slotGains.pact, 'Pact level')}</span></span></li>}
      {unlocksSubclass && <li><i className="fa-solid fa-code-branch" aria-hidden="true"></i>A subclass choice</li>}
    </ul>
  );

  let footer;
  if (step === 'add-class') {
    footer = <><Button variant="ghost" onClick={() => { setStep('choose-progression'); setSelectedNewClassId(''); setValidationError(''); }}>Back</Button><Button variant="primary" onClick={() => setStep('add-review')} disabled={!target}>Next: review</Button></>;
  } else if (step === 'add-review') {
    footer = <><Button variant="ghost" onClick={() => { setStep('add-class'); setValidationError(''); }} disabled={isSubmitting}>Back</Button><Button variant="primary" onClick={addNewClass} disabled={isSubmitting || !target || Boolean(styleError)}>{isSubmitting ? 'Saving…' : `Add ${target?.name || 'class'} 1`}</Button></>;
  } else if (step === 'done') {
    footer = <Button variant="primary" onClick={() => navigate(0)}>Close</Button>;
  } else if (step === 'review') {
    footer = <><Button variant="ghost" onClick={goBack} disabled={isSubmitting}>Back</Button><Button variant="primary" onClick={levelExisting} disabled={isSubmitting || !selectedExisting || Boolean(barbarianValidation) || Boolean(featValidation)}>{isSubmitting ? 'Saving…' : levelUpLabel}</Button></>;
  } else if (step === 'choices') {
    footer = <><Button variant="ghost" onClick={goBack}>Back</Button><Button variant="primary" onClick={goNext} disabled={spellbookInvalid || Boolean(barbarianValidation) || Boolean(styleError) || Boolean(featValidation)}>Next: review</Button></>;
  } else {
    footer = <><Button variant="ghost" onClick={close}>Cancel</Button><Button variant="primary" onClick={goNext} disabled={!selectedExisting}>{hasChoices ? 'Next: choices' : 'Next: review'}</Button></>;
  }

  return (
    <Modal className="dnd-modal modern-modal progression-modal" show={show} onHide={close} centered size="lg" restoreFocus>
      <ModalShell className="progression-shell">
        <ModalHeader
          title={isAdding ? 'Add a class' : `Level up to ${totalLevel + 1}`}
          subtitle={getMulticlassSummary(form)}
        />
        <ModalBody className="progression-body">
          {notification && <Alert variant="success">{notification}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}
          {validationError && <Alert variant="warning">{validationError}</Alert>}
          {stepIndex >= 0 && (
            <ol className="levelup-steps" aria-label="Level up steps">
              {STEPS.map((entry, index) => (
                <li key={entry.key} className={`${index === stepIndex ? 'is-current' : ''} ${index < stepIndex ? 'is-done' : ''} ${entry.key === 'choices' && target && !hasChoices ? 'is-skipped' : ''}`} aria-current={index === stepIndex ? 'step' : undefined}>
                  <span>{index + 1}</span>{entry.label}
                </li>
              ))}
            </ol>
          )}
          {step === 'choose-progression' && (
            <div className="levelup-layout">
              <div className="levelup-options" role="group" aria-label="Progression choices">
                {levelUpClasses.map((entry) => (
                  <button key={entry.name} type="button" className={`levelup-option ${selectedExistingClassId === entry.name ? 'is-selected' : ''}`} aria-pressed={selectedExistingClassId === entry.name} disabled={entry.disabled} onClick={() => { setSelectedExistingClassId(entry.name); setSelectedNewClassId(''); setSubclassChoice(''); setPrimalKnowledgeSkill(''); setValidationError(''); }}>
                    <span className="levelup-option__name">{entry.name}</span>
                    <span className="levelup-option__level">{entry.disabled ? `Level ${entry.level}` : `${entry.level} → ${entry.nextLevel}`}</span>
                    <small>{[entry.subclass, entry.hitDie && `Hit Die ${entry.hitDie}`, entry.disabled && entry.reason].filter(Boolean).join(' · ')}</small>
                  </button>
                ))}
                <button type="button" className="levelup-option levelup-option--add" onClick={() => { setStep('add-class'); setSelectedExistingClassId(''); setValidationError(''); }}>
                  <span className="levelup-option__name">+ Add a class</span>
                  <small>Multiclass into another class</small>
                </button>
              </div>
              <section className="levelup-preview" aria-live="polite" aria-label="Level preview">
                {selectedExisting ? (
                  <>
                    <h3 className="levelup-preview__title">{selectedExisting.name} {selectedExisting.nextLevel} gives you</h3>
                    {renderGains()}
                  </>
                ) : (
                  <p className="levelup-preview__muted">Pick a class to see what the next level gives.</p>
                )}
              </section>
            </div>
          )}
          {step === 'choices' && selectedExisting && (
            <section className="levelup-choices" aria-label="Level up choices">
              {requiresBarbarianPrimalSkill && (
                <Form.Group className="levelup-choice" controlId="levelup-primal-knowledge">
                  <Form.Label>Primal Knowledge skill</Form.Label>
                  <p className="levelup-choice__hint">Required. You gain proficiency in one more Barbarian skill.</p>
                  <Form.Select value={primalKnowledgeSkill} onChange={(event) => setPrimalKnowledgeSkill(event.target.value)}>
                    <option value="" disabled>Select one skill</option>
                    {availablePrimalSkills.map((skill) => <option key={skill} value={skill}>{skillLabels[skill] || skill}</option>)}
                  </Form.Select>
                </Form.Group>
              )}
              {subclassOptions.length > 0 && (
                <div className="levelup-choice" role="radiogroup" aria-label={`${selectedExisting.name} subclass`}>
                  <span className="form-label">{selectedExisting.name} subclass</span>
                  <p className="levelup-choice__hint">Pick now, or decide later in Character Overview.</p>
                  <div className="levelup-subclasses">
                    {subclassOptions.map((option) => (
                      <button key={option.definitionId || option.id} type="button" role="radio" aria-checked={subclassChoice === option.id} className={`levelup-option ${subclassChoice === option.id ? 'is-selected' : ''}`} onClick={() => setSubclassChoice(option.id)}>
                        <span className="levelup-option__name">{option.name}</span>
                        {option.description && <small>{option.description}</small>}
                      </button>
                    ))}
                    <button type="button" role="radio" aria-checked={!subclassChoice} className={`levelup-option levelup-option--add ${!subclassChoice ? 'is-selected' : ''}`} onClick={() => setSubclassChoice('')}>
                      <span className="levelup-option__name">Decide later</span>
                    </button>
                  </div>
                </div>
              )}
              {choosesHuntersPrey && (
                <div className="levelup-choice" role="radiogroup" aria-label="Hunter's Prey">
                  <span className="form-label">Hunter's Prey</span>
                  <p className="levelup-choice__hint">You can switch to the other option after any Short or Long Rest.</p>
                  <div className="levelup-subclasses">
                    {HUNTERS_PREY_OPTIONS.map((option) => (
                      <button key={option.id} type="button" role="radio" aria-checked={huntersPreyChoice === option.id} className={`levelup-option ${huntersPreyChoice === option.id ? 'is-selected' : ''}`} onClick={() => setHuntersPreyChoice(option.id)}>
                        <span className="levelup-option__name">{option.name}</span>
                        <small>{option.summary}</small>
                      </button>
                    ))}
                    <button type="button" role="radio" aria-checked={!huntersPreyChoice} className={`levelup-option levelup-option--add ${!huntersPreyChoice ? 'is-selected' : ''}`} onClick={() => setHuntersPreyChoice('')}>
                      <span className="levelup-option__name">Decide later</span>
                    </button>
                  </div>
                </div>
              )}
              {choosesLand && (
                <div className="levelup-choice" role="radiogroup" aria-label="Land">
                  <span className="form-label">Land</span>
                  <p className="levelup-choice__hint">Your Circle Spells come from this land. You can choose another after each Long Rest.</p>
                  <div className="levelup-subclasses">
                    {Object.values(LANDS).map((land) => (
                      <button key={land.id} type="button" role="radio" aria-checked={landChoice === land.id} className={`levelup-option ${landChoice === land.id ? 'is-selected' : ''}`} onClick={() => setLandChoice(land.id)}>
                        <span className="levelup-option__name">{land.name}</span>
                        <small>{land.spells[3].map((id) => spellData[id]?.name || id).join(', ')}</small>
                      </button>
                    ))}
                    <button type="button" role="radio" aria-checked={!landChoice} className={`levelup-option levelup-option--add ${!landChoice ? 'is-selected' : ''}`} onClick={() => setLandChoice('')}>
                      <span className="levelup-option__name">Decide later</span>
                    </button>
                  </div>
                </div>
              )}
              {gainsForms && (
                <div className="levelup-choice" aria-label="Wild Shape forms">
                  <span className="form-label">Wild Shape forms</span>
                  <p className="levelup-choice__hint">Choose the Beasts you can turn into, or decide later in Character Features.</p>
                  <KnownFormsChooser key={previewKey} form={form} characterId={params.id} forNextLevel initialPicks={formDraft.picks} onPicksChange={(picks, ready) => setFormDraft({ picks, ready })} />
                </div>
              )}
              {offersStyle && renderStyleChoice()}
              {gainsFeat && <LevelUpFeatChoice form={form} nextForm={nextForm} onChange={setLevelFeat} />}
              {featValidation && <p className="text-danger small" role="alert">{featValidation}</p>}
              {gainsMastery && <LevelUpMasteryChoice form={form} nextForm={nextForm} sourceId={selectedClassId} onChange={setMasterySelections} />}
              {gainsSpellbook && <LevelUpSpellbookChoice nextForm={spellbookForm} picks={spellbookPicks} onChange={setSpellbookPicks} />}
              {gainsSpells && <LevelUpSpellChoice room={spellRoom} picks={spellPicks} onChange={setSpellPicks} />}
              {unlocksScholar && (
                <div className="levelup-choice" role="group" aria-label="Scholar">
                  <span className="form-label">Scholar</span>
                  <p className="levelup-choice__hint">Expertise in one skill you are proficient in. Optional now: you can also choose it in Skills.</p>
                  {scholarOptions.length ? <Form.Group className="mb-2" controlId="levelup-scholar-expertise">
                    <Form.Label>Expertise skill</Form.Label>
                    <Form.Select value={scholarExpertise} onChange={(event) => setScholarExpertise(event.target.value)}>
                      <option value="">Decide later</option>
                      {scholarOptions.map(({ key, label }) => <option key={key} value={key}>{label}</option>)}
                    </Form.Select>
                  </Form.Group> : <p className="levelup-choice__hint">You aren’t proficient in Arcana, History, Investigation, Medicine, Nature or Religion yet.</p>}
                </div>
              )}
              {unlocksDeftExplorer && (
                <div className="levelup-choice" role="group" aria-label="Deft Explorer">
                  <span className="form-label">Deft Explorer</span>
                  <p className="levelup-choice__hint">Optional now: you can also choose these in Skills and Character Overview.</p>
                  <Form.Group className="mb-2" controlId="levelup-deft-expertise">
                    <Form.Label>Expertise skill</Form.Label>
                    <Form.Select value={deftExpertise} onChange={(event) => setDeftExpertise(event.target.value)}>
                      <option value="">Decide later</option>
                      {deftExpertiseOptions.map(({ key, label }) => <option key={key} value={key}>{label}</option>)}
                    </Form.Select>
                  </Form.Group>
                  {deftLanguages.map((choice, index) => (
                    <Form.Group className="mb-2" controlId={`levelup-deft-language-${index}`} key={index}>
                      <Form.Label>{`Language ${index + 1}`}</Form.Label>
                      <Form.Select value={choice} onChange={(event) => setDeftLanguages((previous) => previous.map((entry, i) => (i === index ? event.target.value : entry)))}>
                        <option value="">Decide later</option>
                        {deftLanguageOptions.map((language) => <option key={language} value={language} disabled={deftLanguages.includes(language) && choice !== language}>{language}</option>)}
                      </Form.Select>
                    </Form.Group>
                  ))}
                </div>
              )}
            </section>
          )}
          {step === 'review' && selectedExisting && (
            <section className="levelup-preview levelup-review" aria-label="Level up review">
              <h3 className="levelup-preview__title">{selectedExisting.name} {selectedExisting.level} → {selectedExisting.nextLevel}</h3>
              {renderGains({ details: true })}
              {(requiresBarbarianPrimalSkill || unlocksSubclass || unlocksDeftExplorer || offersStyle || gainsFeat || gainsMastery || gainsSpellbook || gainsSpells || unlocksScholar) && (
                <dl className="levelup-review__choices">
                  {requiresBarbarianPrimalSkill && <><dt>Primal Knowledge</dt><dd>{skillLabels[primalKnowledgeSkill] || primalKnowledgeSkill}</dd></>}
                  {unlocksSubclass && <><dt>Subclass</dt><dd>{chosenSubclass ? chosenSubclass.name : 'Decide later in Character Overview'}{chosenSubclass?.description && <small className="levelup-preview__detail">{chosenSubclass.description}</small>}</dd></>}
                  {offersStyle && <><dt>Fighting Style</dt><dd>{styleSummary}</dd></>}
                  {choosesHuntersPrey && <><dt>Hunter's Prey</dt><dd>{chosenPrey ? chosenPrey.name : 'Decide later in Character Features'}{chosenPrey?.summary && <small className="levelup-preview__detail">{chosenPrey.summary}</small>}</dd></>}
                  {choosesLand && <><dt>Land</dt><dd>{chosenLand ? chosenLand.name : 'Decide later in Character Features'}</dd></>}
                  {gainsForms && <><dt>Wild Shape forms</dt><dd>{formPicks ? `${formPicks.length} forms chosen` : 'Decide later in Character Features'}</dd></>}
                  {unlocksDeftExplorer && <><dt>Expertise</dt><dd>{deftExpertise ? (skillLabels[deftExpertise] || deftExpertise) : 'Decide later in Skills'}</dd></>}
                  {unlocksDeftExplorer && <><dt>Languages</dt><dd>{deftLanguagesReady ? deftLanguages.join(', ') : 'Decide later in Character Overview'}</dd></>}
                  {gainsFeat && <><dt>Feat</dt><dd>{levelFeat?.ready ? levelFeat.definition.featName : 'Decide later in Feats'}{levelFeat?.ready && levelFeat.definition.notes && <small className="levelup-preview__detail">{levelFeat.definition.notes}</small>}</dd></>}
                  {gainsSpellbook && <><dt>Spellbook</dt><dd>{spellbookPicks.length ? spellbookPicks.map((id) => spellData[id]?.name || id).join(', ') : 'Decide later in Character Features'}</dd></>}
                  {gainsSpells && <><dt>Spells</dt><dd>{spellPickCount ? [...spellPicks.cantrips, ...spellPicks.spells].map((id) => spellData[id]?.name || id).join(', ') : 'Decide later on the Spells page'}</dd></>}
                  {unlocksScholar && <><dt>Scholar</dt><dd>{scholarExpertise ? (skillLabels[scholarExpertise] || scholarExpertise) : 'Decide later in Skills'}</dd></>}
                  {gainsMastery && <><dt>Weapon Mastery</dt><dd>{masterySelections ? `${masterySelections.length} weapons` : 'Decide later in Character Features'}</dd></>}
                </dl>
              )}
              {laterReminders.length > 0 && (
                <div className="levelup-review__later">
                  <span>After leveling</span>
                  <ul>{laterReminders.map((reminder) => <li key={reminder}>{reminder}</li>)}</ul>
                </div>
              )}
            </section>
          )}
          {step === 'add-class' && (
            <div className="levelup-layout">
              <div className="levelup-options">
                <SearchBar value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search classes" aria-label="Search classes" />
                <div className="levelup-options__scroll" role="group" aria-label="Classes to add">
                  {filteredNewClasses.map((entry) => (
                    <button key={entry.name} type="button" className={`levelup-option ${selectedNewClassId === entry.name ? 'is-selected' : ''}`} aria-pressed={selectedNewClassId === entry.name} disabled={entry.disabled} onClick={() => { setSelectedNewClassId(entry.name); setValidationError(''); }}>
                      <span className="levelup-option__name">{entry.name}</span>
                      {entry.hitDie && <span className="levelup-option__level">{String(entry.hitDie).startsWith('d') ? entry.hitDie : `d${entry.hitDie}`}</span>}
                      <small>{entry.disabled ? `Already level ${entry.currentLevel}` : entry.multiclassRequirement ? `Requires ${entry.multiclassRequirement}` : 'No ability requirement'}</small>
                    </button>
                  ))}
                  {!filteredNewClasses.length && <p className="levelup-preview__muted">No classes match "{search}".</p>}
                </div>
              </div>
              <section className="levelup-preview" aria-live="polite" aria-label="Level preview">
                {target ? (
                  <>
                    <h3 className="levelup-preview__title">{target.name} 1 gives you</h3>
                    {renderGains()}
                    <p className="levelup-preview__note">
                      {requirementNote ? `${requirementNote} ` : ''}
                      Multiclassing gives only some of a class's starting proficiencies.
                    </p>
                  </>
                ) : (
                  <p className="levelup-preview__muted">Pick a class to see what it gives at level 1.</p>
                )}
              </section>
            </div>
          )}
          {step === 'add-review' && target && (
            <section className="levelup-preview levelup-review" aria-label="Level up review">
              <h3 className="levelup-preview__title">Add {target.name} 1 · total level {totalLevel} → {totalLevel + 1}</h3>
              {renderGains({ details: true })}
              {offersStyle && renderStyleChoice()}
              {gainsSpells && <LevelUpSpellChoice room={spellRoom} picks={spellPicks} onChange={setSpellPicks} />}
              {requirementNote && <p className="levelup-preview__note">{requirementNote}</p>}
              {laterReminders.length > 0 && (
                <div className="levelup-review__later">
                  <span>After adding the class</span>
                  <ul>{laterReminders.map((reminder) => <li key={reminder}>{reminder}</li>)}</ul>
                </div>
              )}
            </section>
          )}
        </ModalBody>
        <ModalFooter className="progression-footer">
          {footer}
        </ModalFooter>
      </ModalShell>
    </Modal>
  );
}
